import { useMemo, FC } from "react";
import * as T from "types";
import * as Ui from "semantic-ui-react";
import { Link } from "react-router-dom";
import { BucketLink, ConfluenceChannelsLink } from "components/BucketLink";
import {
  groupArchivedChannelsByDate,
  categorizedActiveChannels,
} from "./Channels.utils";
import { sortChannels } from "utils/channelCategories";
import { ArchivedChannel } from "@fotokite/archive-admin-api";

interface Props {
  allObjects: T.StorageMetadataList;
  archivedChannels: ArchivedChannel[];
}

export const Channels: FC<Props> = (p) => {
  // Flatten the archived channels to a simple array of channel names
  const flatArchivedChannels = useMemo(() => {
    return p.archivedChannels.map((channel) => {
      return channel.channel ?? "";
    });
  }, [p.archivedChannels]);

  const filteredChannels = useMemo(() => {
    return T.groupChannels(p.allObjects).reduce((acc: string[], group) => {
      const first = group.first(null);
      if (!first) return acc;

      return [...acc, first.fkChannel];
    }, []);
  }, [p]);

  const activeChannels = useMemo(() => {
    return categorizedActiveChannels(filteredChannels, flatArchivedChannels);
  }, [filteredChannels, flatArchivedChannels]);

  const archivedChannels = useMemo(() => {
    return groupArchivedChannelsByDate(p.archivedChannels);
  }, [p.archivedChannels]);

  return (
    <div>
      <ConfluenceChannelsLink />
      <BucketLink />
      <Ui.Container style={{ marginTop: "20px" }}>
        <Ui.Header as="h2">Active Channels:</Ui.Header>
        <Ui.Divider />
        {Object.entries(activeChannels).map(([groupKey, groupData]) => {
          return (
            <div key={`channel_group-${groupKey}`}>
              {groupData.channels.size !== 0 && (
                <Ui.Header as="h4" style={{ margin: "10px 0 4px 0" }}>
                  {groupData.label}:
                </Ui.Header>
              )}

              <div style={{ marginLeft: "1.5em" }}>
                {Array.from(groupData.channels)
                  .sort(sortChannels)
                  .map((channel) => {
                    return (
                      <ChannelItem
                        key={channel}
                        channel={channel}
                        active={true}
                      />
                    );
                  })}
              </div>
            </div>
          );
        })}
      </Ui.Container>
      <br></br>
      <Ui.Container style={{ marginTop: "20px" }}>
        <Ui.Header as="h2">Archived Channels:</Ui.Header>
        <Ui.Divider />
        {Object.entries(archivedChannels).map(([date, channels]) => {
          return (
            <div key={date}>
              <Ui.Header as="h4">Archived on {date}</Ui.Header>
              {channels.map((channel) => (
                <ChannelItem
                  key={channel.channel}
                  channel={channel.channel ?? ""}
                  active={false}
                />
              ))}
            </div>
          );
        })}
      </Ui.Container>
    </div>
  );
};

interface IChannelProps {
  channel: string;
  active: boolean;
}
export const ChannelItem: FC<IChannelProps> = ({ channel, active }) => {
  return (
    <Ui.Item>
      <Ui.Item.Content>
        <Ui.Item.Header>
          <Link
            to={`/channel/${channel}`}
            style={{ color: !active ? "#a1b4c6" : "", fontSize: "1.3em" }}
          >
            {channel}
          </Link>
        </Ui.Item.Header>
      </Ui.Item.Content>
    </Ui.Item>
  );
};
